var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { forwardRef } from 'react';
import { Listbox } from '@headlessui/react';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import { CheckIcon, SelectorIcon } from '../icons/index.js';
import { FloatingContextProvider, FloatingWrapper, useFloatingContext, } from '../utils/floating/index.js';
import { runIfFn } from '../utils/index.js';
import { SelectProvider, useSelect } from './SelectContext.js';
export const Select = (props) => {
    const { children, className, value, placeholder, items, invalid = false } = props, rest = __rest(props, ["children", "className", "value", "placeholder", "items", "invalid"]);
    return (React.createElement(Listbox, Object.assign({}, rest, { value: value }), renderProps => {
        var _a;
        let content;
        if (items) {
            content = (React.createElement(React.Fragment, null,
                React.createElement(SelectButton, { placeholder: placeholder }, ((_a = items.find(item => value === item.value)) === null || _a === void 0 ? void 0 : _a.text) || null),
                React.createElement(SelectItems, null, items.map(item => (React.createElement(SelectItem, { key: item.value, value: item.value }, item.text))))));
        }
        else {
            content = runIfFn(children, renderProps);
        }
        return (React.createElement("div", { className: twMerge(clsx('relative isolate', className)) },
            React.createElement(SelectProvider, { open: renderProps.open, invalid: invalid },
                React.createElement(FloatingContextProvider, { placement: "bottom-start" }, content))));
    }));
};
const SelectButton = props => {
    const { children, className, placeholder, arrowsClassname } = props, rest = __rest(props, ["children", "className", "placeholder", "arrowsClassname"]);
    const { invalid } = useSelect();
    const { reference } = useFloatingContext();
    return (React.createElement("div", { ref: reference },
        React.createElement(Listbox.Button, Object.assign({ className: twMerge(clsx('relative w-full rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default', 'focus:outline-none focus-visible:ring', !invalid && 'border border-basicSurface-300/25', invalid &&
                'border border-danger-300 text-danger-900 placeholder-danger-300 focus:outline-none focus:ring-danger-500 focus:border-danger-500', 'text-basicSurface-500 bg-surface-50', className)) }, rest), ({ disabled }) => (React.createElement(React.Fragment, null,
            React.createElement("span", { className: clsx('block truncate', disabled ? 'opacity-50' : '') }, children || (React.createElement("div", { className: "text-basicSurface-300" }, placeholder || '\u00A0'))),
            React.createElement("span", { className: clsx('absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none', arrowsClassname) },
                React.createElement(SelectorIcon, { className: "w-5 h-5 text-basicSurface-400", "aria-hidden": "true" })))))));
};
const SelectItems = forwardRef((props, ref) => {
    const { children, className } = props, rest = __rest(props, ["children", "className"]);
    const { open } = useSelect();
    return (React.createElement(FloatingWrapper, { open: open, sameWidth: true },
        React.createElement(Listbox.Options, Object.assign({ className: twMerge(clsx('w-full min-w-[160px] text-basicSurface-500 bg-surface-50 shadow-lg max-h-60 rounded-md py-1 text-base border border-surface-200 overflow-auto', 'focus-visible:ring-1 focus:outline-none', className)) }, rest, { ref: ref }), children)));
});
const SelectItem = props => {
    const { value, children, disabled, className } = props, rest = __rest(props, ["value", "children", "disabled", "className"]);
    return (React.createElement(Listbox.Option, Object.assign({ className: ({ active }) => clsx(active
            ? 'text-basicPrimary-500 bg-actionPrimary-500'
            : 'text-basicSurface-900', 'cursor-default select-none relative py-2 pl-3 pr-9', className), value: value, disabled: disabled }, rest), ({ selected, active }) => (React.createElement(React.Fragment, null,
        React.createElement("span", { className: clsx(selected ? 'font-semibold' : 'font-normal', disabled ? 'opacity-50' : '', 'block truncate') }, runIfFn(children, {
            selected,
            active,
        })),
        selected ? (React.createElement("span", { className: clsx(active ? 'text-basicPrimary-500' : 'text-actionPrimary-600', 'absolute inset-y-0 right-0 flex items-center pr-4') },
            React.createElement(CheckIcon, { className: "h-5 w-5", "aria-hidden": "true" }))) : null))));
};
Select.Button = SelectButton;
Select.Items = SelectItems;
Select.Item = SelectItem;
//# sourceMappingURL=Select.js.map